import React, { useCallback } from 'react';
import {
  addEventOccurrence,
  addManyEventOccurrences,
  createEventRequest,
  popAdminPanel,
  removeAllEventOccurrences,
  removeEventOccurrence,
  setEventDescription,
  setEventName,
  setEventPageId,
  setEventPageTitle,
  setEventPlaceId,
  setEventPlaceName,
  setEventPosterImages,
  setEventTags,
  updateEventRequest,
} from '../../../actions';
import {
  eventHasId,
  getEventDescription,
  getEventName,
  getEventOccurrences,
  getEventPageId,
  getEventPageTitle,
  getEventPlaceId,
  getEventPlaceName,
  getEventPosterImage,
  getEventTags,
} from '../../../selectors';
import { useDispatch, useSelector } from 'react-redux';
import { PosterImageType } from '../../../state';

export const useAdminEventPanelController = (eventKey: string) => {
  const dispatch = useDispatch();

  const name = useSelector(getEventName(eventKey));
  const description = useSelector(getEventDescription(eventKey));
  const editingEvent = useSelector(eventHasId(eventKey));
  const tags = useSelector(getEventTags(eventKey));
  const occurrences = useSelector(getEventOccurrences(eventKey));

  const pageId = useSelector(getEventPageId(eventKey));
  const pageTitle = useSelector(getEventPageTitle(eventKey));

  const placeId = useSelector(getEventPlaceId(eventKey));
  const placeName = useSelector(getEventPlaceName(eventKey));

  const posterImage = useSelector(getEventPosterImage(eventKey));
  const posterImageKey = posterImage ? posterImage.imageKey : undefined;

  const onNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(setEventName(eventKey, e.target.value));
    },
    [eventKey],
  );

  const onDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      dispatch(setEventDescription(eventKey, e.target.value));
    },
    [eventKey],
  );

  const onTagsChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      dispatch(setEventTags(eventKey, e.target.value));
    },
    [eventKey],
  );

  const onSaveClick = useCallback(() => {
    if (editingEvent) {
      dispatch(updateEventRequest(eventKey));
    } else {
      dispatch(createEventRequest(eventKey));
    }
    dispatch(popAdminPanel());
  }, [eventKey, editingEvent]);

  const onDiscardClick = useCallback(() => {
    dispatch(popAdminPanel());
  }, [eventKey]);

  const onPosterImageUploadStart = useCallback(
    (imageKey: string) => {
      dispatch(
        setEventPosterImages(eventKey, [
          { type: PosterImageType.Generic, imageKey },
        ]),
      );
    },
    [eventKey],
  );

  const onPageSelect = useCallback(
    (page: { id: string; title: string }) => {
      dispatch(setEventPageId(eventKey, page.id));
      dispatch(setEventPageTitle(eventKey, page.title));
    },
    [eventKey],
  );

  const onPlaceSelect = useCallback(
    (place: { id: string; name: string }) => {
      dispatch(setEventPlaceId(eventKey, place.id));
      dispatch(setEventPlaceName(eventKey, place.name));
    },
    [eventKey],
  );

  const onAddOccurrenceClick = useCallback(() => {
    dispatch(addEventOccurrence(eventKey));
  }, []);

  const onRemoveOccurrenceClick = useCallback((index: number) => {
    dispatch(removeEventOccurrence(eventKey, index));
  }, []);

  const onAddRecurrenceRuleClick = useCallback(
    (occurrences: Date[], duration?: number) => {
      dispatch(removeAllEventOccurrences(eventKey));
      dispatch(
        addManyEventOccurrences(
          eventKey,
          occurrences.map(d => ({ duration, startsAt: d })),
        ),
      );
    },
    [eventKey],
  );

  return {
    description,
    editingEvent,
    name,
    occurrences,
    onAddRecurrenceRuleClick,
    onAddOccurrenceClick,
    onDescriptionChange,
    onDiscardClick,
    onNameChange,
    onPageSelect,
    onPlaceSelect,
    onPosterImageUploadStart,
    onRemoveOccurrenceClick,
    onSaveClick,
    onTagsChange,
    pageId,
    pageTitle,
    placeId,
    placeName,
    posterImageKey,
    tags,
  };
};
